/** Notifications dans l'application et réglages des rappels. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { notificationsApi } from "../api/endpoints";
import { useToast } from "../components/ToastContext";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Chip,
  EmptyState,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
} from "../components/ui";
import { useDocumentTitle, useMutation, useResource } from "../hooks/useApi";
import { formatDateTime } from "../lib/format";

/** Paliers proposés dans l'interface. Les valeurs restent configurables. */
const OFFSET_CHOICES = [30, 15, 7, 3, 1, 0];

function offsetLabel(days: number): string {
  if (days === 0) return "Le jour J";
  if (days === 1) return "1 jour avant";
  return `${days} jours avant`;
}

export function NotificationsPage() {
  useDocumentTitle("Notifications");
  const toast = useToast();
  const [unreadOnly, setUnreadOnly] = useState(false);

  const notifications = useResource(
    () => notificationsApi.list({ unread_only: unreadOnly || undefined, limit: 50 }),
    [unreadOnly],
  );
  const preferences = useResource(() => notificationsApi.preferences(), []);

  const [offsets, setOffsets] = useState<number[]>([]);
  useEffect(() => {
    if (preferences.data) setOffsets(preferences.data.reminder_offsets_days);
  }, [preferences.data]);

  const savePreferences = useMutation(
    (patch: Record<string, unknown>) => notificationsApi.updatePreferences(patch),
    {
      onSuccess: async () => {
        await preferences.reload();
        toast.success("Réglages enregistrés");
      },
      onError: (error) => toast.error("Enregistrement refusé", error.message),
    },
  );

  const markAllRead = useMutation(() => notificationsApi.markAllRead(), {
    onSuccess: async () => {
      await notifications.reload();
      toast.success("Notifications marquées comme lues");
    },
  });

  const markRead = useMutation((id: string) => notificationsApi.markRead(id), {
    onSuccess: () => notifications.reload(),
  });

  const runReminders = useMutation(() => notificationsApi.runReminders(), {
    onSuccess: async (result) => {
      await notifications.reload();
      toast.success(
        `${result.created_notifications} rappel(s) créé(s)`,
        `${result.emails_sent} e-mail(s) envoyé(s). ${result.channel_hint}`,
      );
    },
    onError: (error) => toast.error("Calcul des rappels impossible", error.message),
  });

  const p = preferences.data;
  const offsetsChanged =
    p && JSON.stringify([...offsets].sort()) !== JSON.stringify([...p.reminder_offsets_days].sort());

  return (
    <>
      <PageHeader
        title="Notifications"
        subtitle="Rappels d'échéance et alertes de votre dossier."
        actions={
          <>
            <Button
              variant="ghost"
              loading={runReminders.pending}
              onClick={() => void runReminders.run()}
              title="Recalcule immédiatement les rappels dus, sans attendre la prochaine exécution automatique."
            >
              Vérifier les rappels maintenant
            </Button>
            <Button
              variant="primary"
              loading={markAllRead.pending}
              onClick={() => void markAllRead.run()}
            >
              Tout marquer comme lu
            </Button>
          </>
        }
      />

      <div className="grid grid--sidebar">
        <div className="stack">
          <div className="chips">
            <Chip
              label="Toutes"
              pressed={!unreadOnly}
              onToggle={() => setUnreadOnly(false)}
            />
            <Chip
              label="Non lues seulement"
              pressed={unreadOnly}
              onToggle={() => setUnreadOnly(true)}
            />
          </div>

          <ErrorAlert error={notifications.error} title="Notifications indisponibles" />

          <Card flush>
            {notifications.loading ? (
              <LoadingBlock />
            ) : !notifications.data || notifications.data.items.length === 0 ? (
              <EmptyState title="Aucune notification">
                Les rappels apparaîtront ici à l'approche de vos échéances, selon
                les paliers configurés.
              </EmptyState>
            ) : (
              <ul className="task-list">
                {notifications.data.items.map((notification) => (
                  <li key={notification.id}>
                    <div className="task-item">
                      <div className="task-item__top">
                        <span className="task-item__title">
                          {notification.title}
                        </span>
                        {notification.severity === "critical" && (
                          <span className="badge badge--danger">En retard</span>
                        )}
                        {notification.severity === "warning" && (
                          <span className="badge badge--warning">Imminent</span>
                        )}
                        {!notification.read_at && (
                          <span className="badge badge--accent">Non lue</span>
                        )}
                      </div>
                      {notification.body && (
                        <p className="task-item__why">{notification.body}</p>
                      )}
                      <div className="task-item__meta">
                        <span>{formatDateTime(notification.created_at)}</span>
                        {notification.task_id && (
                          <Link to={`/demarches/${notification.task_id}`}>
                            Ouvrir la démarche
                          </Link>
                        )}
                        {!notification.read_at && (
                          <button
                            type="button"
                            className="btn btn--quiet btn--sm"
                            onClick={() => void markRead.run(notification.id)}
                          >
                            Marquer comme lue
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <div className="stack">
          <Card
            title="Quand souhaitez-vous être prévenu ?"
            hint="Ces paliers sont entièrement configurables."
          >
            {preferences.loading || !p ? (
              <LoadingBlock />
            ) : (
              <>
                <div className="chips">
                  {OFFSET_CHOICES.map((days) => (
                    <Chip
                      key={days}
                      label={offsetLabel(days)}
                      pressed={offsets.includes(days)}
                      onToggle={() =>
                        setOffsets(
                          offsets.includes(days)
                            ? offsets.filter((d) => d !== days)
                            : [...offsets, days],
                        )
                      }
                    />
                  ))}
                </div>
                {offsets.length === 0 && (
                  <Alert tone="warning" title="Aucun palier sélectionné">
                    Sans palier, vous ne recevrez aucun rappel avant l'échéance.
                  </Alert>
                )}
                {offsetsChanged && (
                  <Button
                    variant="primary"
                    block
                    className="mt-4"
                    loading={savePreferences.pending}
                    onClick={() =>
                      void savePreferences.run({ reminder_offsets_days: offsets })
                    }
                  >
                    Enregistrer les paliers
                  </Button>
                )}
              </>
            )}
          </Card>

          {p && (
            <Card title="Canaux et contenus">
              <Checkbox
                label="Notifications dans l'application"
                checked={p.in_app_enabled}
                onChange={(value) =>
                  void savePreferences.run({ in_app_enabled: value })
                }
              />
              <Checkbox
                label="Rappels par e-mail"
                checked={p.email_enabled}
                onChange={(value) => void savePreferences.run({ email_enabled: value })}
              />
              <hr />
              <Checkbox
                label="Me prévenir des démarches en retard"
                description="Un rappel par jour et par démarche, tant qu'elle est en retard."
                checked={p.notify_overdue}
                onChange={(value) => void savePreferences.run({ notify_overdue: value })}
              />
              <Checkbox
                label="Me prévenir des informations manquantes"
                checked={p.notify_missing_information}
                onChange={(value) =>
                  void savePreferences.run({ notify_missing_information: value })
                }
              />
              <Checkbox
                label="Inclure les démarches facultatives"
                description="Par défaut, seules les obligations et démarches recommandées déclenchent un rappel."
                checked={p.notify_optional_tasks}
                onChange={(value) =>
                  void savePreferences.run({ notify_optional_tasks: value })
                }
              />
            </Card>
          )}

          <Card title="Éviter les doublons">
            <p className="text-muted mb-0">
              Chaque rappel est envoyé une seule fois par démarche, par palier et
              par canal. Relancer la vérification ne génère donc pas de doublon.
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}
